/**
 * Card-to-card comparison: where do two versions of the "same" work DIFFER?
 *
 * ## Why this is a separate axis from `strength`
 *
 * `mapping-facets.ts` colours a row by what it contributed to the *match* —
 * information about why the server grouped these items. That is the wrong
 * question for a reader who has already accepted the grouping and now has to
 * pick a version, because it is identical on every card and says nothing about
 * how the cards differ from each other.
 *
 * So each row carries a second mark: is this value the same as the card to its
 * left, or not? Sameness is reassurance; difference is where attention goes.
 * Four cards of a book that agree on everything but the interior PDF should
 * read as exactly that at a glance.
 *
 * ## Why difference is graded rather than flat
 *
 * A difference in a facet the match did NOT rest on is ordinary — four
 * editions legitimately have four interior PDFs. A difference in the facet the
 * match DID rest on is close to a contradiction: the server said these belong
 * together *because* that value matched, and on this card it does not. The
 * second case is rare, serious, and the single best reason to disagree with a
 * proposal, so it gets its own colour rather than being averaged in with the
 * first.
 *
 * - `SAME`       — identical to the previous card. Green.
 * - `DIFF_MINOR` — differs, on a facet the match did not rest on. Orange.
 * - `DIFF_MAJOR` — differs, on a facet the match DID rest on. Pink.
 *
 * ## Deliberately "the previous card", not "the seed"
 *
 * Comparing everything to the seed makes the third and fourth cards describe a
 * relationship the reader is not looking at. Chained comparison matches how
 * the row is actually read — left to right — and makes a run of identical
 * versions appear as an unbroken green column, which is the shape that says
 * "these really are the same thing".
 *
 * No Angular import: loaded by the smoke test under plain ts-node.
 */

import {MappingFacetRow} from './mapping-facets';

export type MappingComparison = 'SAME' | 'DIFF_MINOR' | 'DIFF_MAJOR';

/** A card, as far as comparison is concerned. */
export interface ComparableCard {
  facetRows: MappingFacetRow[];
  /** The item's own id, for the id-versus-date sequence check. */
  item_ID?: number;
  /** `BOOK` or `DRAFT`, for the same-kind fallback below. */
  itemType?: string;
  /**
   * The sibling this item was actually compared against, from the payload.
   *
   * **This is the anchor, and it is not the card to the left.** The server
   * sets it per member (planner 1.2.0): each Book against the previous Book,
   * but **the first Draft against the primary BOOK** - skipping over any
   * later Books in between. Positional chaining gets that one case wrong, and
   * it is a common case.
   *
   * Three distinct values, all meaningful:
   *   - an object  - compare against that item
   *   - `null`     - the SEED; it was compared against nothing
   *   - `undefined`- the payload said nothing; fall back to the previous card
   *                  of the same kind
   */
  joinedTo?: { itemType: string, item_ID: number } | null;
  /** Epoch ms, 0 when unknown. */
  lastUpdateMs?: number;
  /** Set by {@link markSequenceAnomalies}. */
  sequenceAnomaly?: boolean;
}

/**
 * Facet strengths that make a DIFFERENCE serious.
 *
 * `DECISIVE` is the facet the match rested on. `DIFFERS` is the server already
 * telling us this value does not match (`BODY_RESOURCE_ONLY` — "same interior
 * PDF, titles differ"), so it is a known contradiction rather than a
 * discovered one and belongs in the same bucket.
 */
/**
 * Facets where a DIFFERENCE is serious whatever the join reason said.
 *
 * `bindingType` is a **hard partition key** server-side: two items whose
 * binding differs are never compared by the clusterer at all, which is why
 * `PARTITION_MISMATCH` needed its own scan to find them. So a binding
 * difference *inside* one proposal is not an ordinary variation - it means the
 * group was formed on something that bypassed the partition (an existing
 * `work_ID`, or a human edit), and it is the reader's single clearest signal
 * that these are two orderable products rather than two versions of one.
 *
 * Paper and lamination are deliberately NOT here. They are real differences
 * and worth showing - a reader choosing between two editions wants to know -
 * but they are finishing choices, not identity, and flagging them at the same
 * severity as binding would flatten exactly the distinction that makes binding
 * worth flagging.
 */
const STRUCTURAL_FACETS: string[] = ['BINDING'];

/**
 * Facets whose numbers are issued in ascending order, so a difference has a
 * DIRECTION as well as a magnitude.
 *
 * Resource ids only. Pages and trim differ without one being "newer", and
 * saying so would read as a claim about provenance that nothing supports.
 */
const ORDINAL_FACETS: string[] = ['BODY', 'COVER'];

function isMajor(row: MappingFacetRow): boolean {
  return row.strength === 'DECISIVE'
      || row.strength === 'DIFFERS'
      || STRUCTURAL_FACETS.indexOf(row.key) >= 0;
}

/**
 * Mark every row on every card with how it compares to the card before it.
 *
 * Mutates the rows. The first card is the baseline and is left unmarked — it
 * has nothing to its left, and colouring it would imply a comparison that was
 * never made.
 *
 * **A row is only compared when BOTH values are present.** An empty value
 * means "not known", not "not equal": two blanks are not evidence of agreement
 * and a blank against a value is not evidence of conflict. Marking either
 * would turn missing data into a claim, which on a screen whose whole job is
 * disclosure is the one thing it must not do.
 */
export function markComparisons(cards: ComparableCard[]): void {
  if ( !cards || cards.length === 0 ) {
    return;
  }
  const byKey: { [key: string]: ComparableCard } = {};
  for ( const card of cards ) {
    byKey[cardKey(card.itemType, card.item_ID)] = card;
  }
  for ( let i = 0; i < cards.length; i++ ) {
    const card = cards[i];
    for ( const row of card.facetRows || [] ) {
      row.comparison = undefined;
      row.direction = undefined;
    }
    const reference = referenceFor(card, byKey, cards, i);
    if ( reference && reference !== card ) {
      compareCard(card, reference);
    }
  }
}

function cardKey(itemType: string, item_ID: number): string {
  return '' + itemType + ':' + item_ID;
}

/**
 * What one member was compared against.
 *
 * Driven by `joinedTo` rather than by position, because the two disagree in a
 * case that is both common and wrong-looking: the first Draft is joined to the
 * **primary Book**, which may be several cards to its left with other Books in
 * between. Chaining positionally compared it with the *last* Book instead, so
 * a Draft identical to the primary Book was drawn with a differing title and a
 * differing cover - contradicting the `IDENTICAL` sentence printed on the same
 * card. Reported 2026-10-26, from a real card on "Hungry For God".
 *
 * Using the payload's own anchor also means the grid and the reason sentence
 * can never again say different things, because they are now the same fact.
 */
function referenceFor(card: ComparableCard,
                      byKey: { [key: string]: ComparableCard },
                      cards: ComparableCard[],
                      index: number): ComparableCard {
  if ( card.joinedTo ) {
    // Named explicitly. If it is not in this proposal we mark nothing rather
    // than guess - a wrong anchor is worse than none.
    return byKey[cardKey(card.joinedTo.itemType, card.joinedTo.item_ID)] || null;
  }
  if ( card.joinedTo === null ) {
    return null;   // the SEED, compared against nothing
  }
  // Nothing said. The previous card of the same kind is the server's own rule
  // for every non-first item, so it is the best available assumption.
  for ( let i = index - 1; i >= 0; i-- ) {
    if ( cards[i].itemType === card.itemType ) {
      return cards[i];
    }
  }
  return null;
}

/**
 * Compare every card against ONE fixed reference, rather than against its
 * neighbour.
 *
 * This is what near misses need. A rejected candidate is not one of a sequence
 * of versions - it is a single item held up against the group - so the only
 * comparison that answers anything is against the version the group is
 * keeping. Chaining them to each other instead produced two defects at once:
 * the first near miss became a baseline and was drawn with **no marks at all**
 * (so a proposal with a single near miss showed an entirely uncoloured card),
 * and the second was measured against the first, which the reader is not
 * comparing it to and which is not in the group either.
 *
 * The reference itself is left untouched - it belongs to the member row above
 * and is already marked relative to its own neighbour.
 */
export function markComparisonsAgainst(reference: ComparableCard, cards: ComparableCard[]): void {
  if ( !reference ) {
    return;
  }
  for ( const card of (cards || []) ) {
    compareCard(card, reference);
  }
}

/** Mark one card's rows against another's. */
function compareCard(card: ComparableCard, against: ComparableCard): void {
  const previous = (against && against.facetRows) || [];
  for ( const row of card.facetRows || [] ) {
    row.comparison = undefined;
    row.direction = undefined;
    const before = previous.filter(r => r.key === row.key)[0];
    if ( !before || !row.value || !before.value ) {
      continue;
    }
    if ( row.value === before.value ) {
      row.comparison = 'SAME';
      // A measured agreement overrules a claim inferred from the reason code.
      //
      // `BODY_RESOURCE_ONLY` means "same interior PDF, titles differ", so the
      // facet table marks TITLE as `DIFFERS`. But that is a statement about
      // the pairing the SERVER made, and the card being compared here may not
      // be that pairing - and with `display` we now hold both titles and can
      // simply look. Where the values agree, they agree; keeping the inherited
      // mark would print "differs" under two identical strings, which is the
      // one thing a disclosure screen must never do.
      if ( row.strength === 'DIFFERS' ) {
        row.strength = 'NEUTRAL';
      }
    } else {
      row.comparison = isMajor(row) ? 'DIFF_MAJOR' : 'DIFF_MINOR';
      // "Newer" or "older", not merely "differs", where the numbers carry an
      // order. Needs BOTH raw numbers: `value` is localized for display and
      // cannot be compared arithmetically.
      if ( ORDINAL_FACETS.indexOf(row.key) >= 0
           && row.numericValue > 0 && before.numericValue > 0 ) {
        row.direction = row.numericValue > before.numericValue ? 'NEWER' : 'OLDER';
      }
    }
  }
}

/**
 * Flag items whose id order and edit order disagree.
 *
 * ## Why this is worth saying out loud
 *
 * A higher id means "created later" and a later `lastUpdate` means "edited
 * more recently", and the two usually agree. When they do not, the two rules
 * the server uses to pick a primary — `RECENCY` (most recently edited) and
 * `ID` (newest version) — point at **different items**, and the proposed
 * primary is only as good as whichever rule happened to be applied. That is
 * precisely the case `PRIMARY_AMBIGUOUS` exists for, and precisely the case a
 * human should look at rather than accept.
 *
 * Reading the ids in ascending order, the dates should never go backwards. Any
 * item whose date is older than something created before it is flagged.
 *
 * Items with no usable date are skipped entirely: `0` means unknown, and an
 * unknown date is not evidence of anything. Including them would flag every
 * item after an unresolved one.
 */
export function markSequenceAnomalies(cards: ComparableCard[]): void {
  for ( const card of (cards || []) ) {
    card.sequenceAnomaly = false;
  }
  const dated = (cards || []).filter(c => c.lastUpdateMs > 0 && c.item_ID > 0);
  if ( dated.length < 2 ) {
    return;
  }
  const byId = dated.slice().sort((a, b) => a.item_ID - b.item_ID);
  let highWater = byId[0].lastUpdateMs;
  for ( let i = 1; i < byId.length; i++ ) {
    if ( byId[i].lastUpdateMs < highWater ) {
      byId[i].sequenceAnomaly = true;
    } else {
      highWater = byId[i].lastUpdateMs;
    }
  }
}
